import {
  ArrowUpRight,
  Check,
  CheckCircle2,
  Clipboard,
  Download,
  Link2,
  LoaderCircle,
  LockKeyhole,
  QrCode,
  Share2,
  ShieldCheck,
  Smartphone,
  Sparkles,
} from 'lucide-react';
import QRCode from 'qrcode';
import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { type Locale, type ToastKey, translations } from './i18n';
import { normalizeWebUrl, qrFileName, type UrlErrorCode } from './lib/url';

type Toast = {
  key: ToastKey;
  tone: 'success' | 'info';
};

type FeatureProps = {
  icon: ReactNode;
  title: string;
  detail: string;
};

const localeStorageKey = 'easyqr-locale';

function Feature({ icon, title, detail }: FeatureProps) {
  return (
    <div className="feature">
      <span className="feature__icon" aria-hidden="true">
        {icon}
      </span>
      <span>
        <strong>{title}</strong>
        <small>{detail}</small>
      </span>
    </div>
  );
}

function getInitialLocale(): Locale {
  try {
    return window.localStorage.getItem(localeStorageKey) === 'en' ? 'en' : 'vi';
  } catch {
    return 'vi';
  }
}

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const helper = document.createElement('textarea');
  helper.value = value;
  helper.setAttribute('readonly', '');
  helper.style.position = 'fixed';
  helper.style.opacity = '0';
  document.body.appendChild(helper);
  helper.select();
  const copied = document.execCommand('copy');
  helper.remove();

  if (!copied) {
    throw new Error('Copy failed');
  }
}

export default function App() {
  const [locale, setLocale] = useState<Locale>(getInitialLocale);
  const [input, setInput] = useState('');
  const [fieldError, setFieldError] = useState<UrlErrorCode | null>(null);
  const [hasResultError, setHasResultError] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultRef = useRef<HTMLElement>(null);
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const t = translations[locale];

  useEffect(() => {
    document.documentElement.lang = locale;
    document.title = t.meta.title;
    document.querySelector('meta[name="description"]')?.setAttribute('content', t.meta.description);
    document.querySelector('meta[property="og:title"]')?.setAttribute('content', t.meta.title);
    document.querySelector('meta[property="og:description"]')?.setAttribute('content', t.meta.description);

    try {
      window.localStorage.setItem(localeStorageKey, locale);
    } catch {
      // The interface still works when browser storage is unavailable.
    }
  }, [locale, t]);

  useEffect(() => {
    if (!toast) return;

    const timeout = window.setTimeout(() => setToast(null), 3600);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  function validateField() {
    if (!input.trim()) {
      setFieldError(null);
      return;
    }

    const result = normalizeWebUrl(input);
    setFieldError(result.ok ? null : result.code);
  }

  async function handleGenerate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setHasResultError(false);

    const normalized = normalizeWebUrl(input);
    if (!normalized.ok) {
      setFieldError(normalized.code);
      inputRef.current?.focus();
      return;
    }

    setFieldError(null);
    setInput(normalized.value);
    setIsGenerating(true);

    try {
      const image = await QRCode.toDataURL(normalized.value, {
        width: 720,
        margin: 4,
        errorCorrectionLevel: 'H',
        color: {
          dark: '#111827',
          light: '#ffffff',
        },
      });

      setGeneratedUrl(normalized.value);
      setQrDataUrl(image);
      setToast({ key: 'ready', tone: 'success' });
      window.requestAnimationFrame(() => resultRef.current?.focus());
    } catch {
      setHasResultError(true);
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleCopy() {
    try {
      await copyText(generatedUrl);
      setToast({ key: 'copied', tone: 'success' });
    } catch {
      setToast({ key: 'copyFailed', tone: 'info' });
    }
  }

  function handleDownload() {
    const anchor = document.createElement('a');
    anchor.href = qrDataUrl;
    anchor.download = qrFileName(generatedUrl);
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setToast({ key: 'downloaded', tone: 'success' });
  }

  async function handleShare() {
    if (!canShare) {
      setToast({ key: 'shareUnavailable', tone: 'info' });
      return;
    }

    try {
      const response = await fetch(qrDataUrl);
      const blob = await response.blob();
      const file = new File([blob], qrFileName(generatedUrl), { type: 'image/png' });
      const fileShare = navigator.canShare?.({ files: [file] });

      if (fileShare) {
        await navigator.share({
          title: t.share.title,
          text: t.share.imageText(generatedUrl),
          files: [file],
        });
      } else {
        await navigator.share({
          title: t.share.title,
          text: t.share.linkText,
          url: generatedUrl,
        });
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      setToast({ key: 'shareFailed', tone: 'info' });
    }
  }

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        {t.skipLink}
      </a>

      <header className="site-header" aria-label={t.header.aria}>
        <a className="brand" href="/" aria-label={t.header.home}>
          <span className="brand__mark" aria-hidden="true">
            <QrCode size={24} strokeWidth={2.2} />
          </span>
          <span>EasyQR</span>
        </a>

        <div className="header-actions">
          <div className="language-switch" role="group" aria-label={t.header.language}>
            <button
              type="button"
              lang="vi"
              aria-label={t.header.vietnamese}
              aria-pressed={locale === 'vi'}
              className={locale === 'vi' ? 'language-switch__button--active' : ''}
              onClick={() => setLocale('vi')}
            >
              VI
            </button>
            <button
              type="button"
              lang="en"
              aria-label={t.header.english}
              aria-pressed={locale === 'en'}
              className={locale === 'en' ? 'language-switch__button--active' : ''}
              onClick={() => setLocale('en')}
            >
              EN
            </button>
          </div>

          <div className="privacy-pill">
            <LockKeyhole size={15} aria-hidden="true" />
            <span>{t.header.privacy}</span>
          </div>
        </div>
      </header>

      <main id="main-content" className="main-content">
        <section className="hero" aria-labelledby="hero-title">
          <div className="eyebrow">
            <Sparkles size={15} aria-hidden="true" />
            {t.hero.eyebrow}
          </div>
          <h1 id="hero-title">
            {t.hero.leading} <span>{t.hero.accent}</span>
          </h1>
          <p>{t.hero.description}</p>
        </section>

        <section className="workspace" aria-label={t.generator.aria}>
          <div className="generator-panel">
            <div className="panel-heading">
              <span className="step-number" aria-hidden="true">1</span>
              <div>
                <p className="panel-kicker">{t.generator.kicker}</p>
                <h2>{t.generator.title}</h2>
              </div>
            </div>

            <form noValidate onSubmit={handleGenerate}>
              <label htmlFor="link-input">{t.generator.label}</label>
              <div className={`input-shell${fieldError ? ' input-shell--error' : ''}`}>
                <Link2 size={20} aria-hidden="true" />
                <input
                  ref={inputRef}
                  id="link-input"
                  name="url"
                  type="url"
                  inputMode="url"
                  autoComplete="url"
                  autoCapitalize="none"
                  spellCheck={false}
                  placeholder={t.generator.placeholder}
                  value={input}
                  aria-invalid={Boolean(fieldError)}
                  aria-describedby="link-helper link-error"
                  onChange={(event) => {
                    setInput(event.target.value);
                    if (fieldError) setFieldError(null);
                  }}
                  onBlur={validateField}
                />
              </div>
              <div className="field-message-row">
                <p id="link-helper" className="field-helper">
                  {t.generator.helper}
                </p>
                <p id="link-error" className="field-error" role="alert">
                  {fieldError ? t.errors[fieldError] : ''}
                </p>
              </div>

              <button
                className="primary-button"
                type="submit"
                disabled={!input.trim() || isGenerating}
              >
                {isGenerating ? (
                  <LoaderCircle className="spinner" size={20} aria-hidden="true" />
                ) : (
                  <QrCode size={20} aria-hidden="true" />
                )}
                <span>{isGenerating ? t.generator.generating : qrDataUrl ? t.generator.update : t.generator.generate}</span>
                {!isGenerating && <ArrowUpRight size={19} aria-hidden="true" />}
              </button>

              <p className="keyboard-hint">{t.generator.tip}</p>
            </form>

            <div className="privacy-note">
              <ShieldCheck size={18} aria-hidden="true" />
              <p>
                <strong>{t.generator.privacyTitle}</strong>
                <span>{t.generator.privacyDetail}</span>
              </p>
            </div>
          </div>

          <section
            ref={resultRef}
            className={`preview-panel${qrDataUrl ? ' preview-panel--ready' : ''}`}
            aria-labelledby="preview-title"
            tabIndex={-1}
          >
            <div className="panel-heading panel-heading--preview">
              <span className="step-number" aria-hidden="true">2</span>
              <div>
                <p className="panel-kicker">{t.preview.kicker}</p>
                <h2 id="preview-title">{t.preview.title}</h2>
              </div>
              {qrDataUrl && (
                <span className="ready-badge">
                  <CheckCircle2 size={15} aria-hidden="true" />
                  {t.preview.ready}
                </span>
              )}
            </div>

            {qrDataUrl ? (
              <div className="result-content">
                <div className="qr-frame">
                  <img
                    src={qrDataUrl}
                    width="720"
                    height="720"
                    alt={t.preview.qrAlt(generatedUrl)}
                  />
                  <span className="scan-line" aria-hidden="true" />
                </div>

                <div className="destination">
                  <span className="destination__icon" aria-hidden="true">
                    <Link2 size={17} />
                  </span>
                  <a href={generatedUrl} target="_blank" rel="noreferrer">
                    {generatedUrl}
                    <ArrowUpRight size={14} aria-hidden="true" />
                  </a>
                </div>

                <div className="result-actions" aria-label={t.preview.actionsAria}>
                  <button className="action-button action-button--strong" type="button" onClick={handleDownload}>
                    <Download size={18} aria-hidden="true" />
                    {t.preview.download}
                  </button>
                  <button className="action-button" type="button" onClick={handleCopy}>
                    <Clipboard size={18} aria-hidden="true" />
                    {t.preview.copy}
                  </button>
                  {canShare && (
                    <button className="action-button" type="button" onClick={handleShare}>
                      <Share2 size={18} aria-hidden="true" />
                      {t.preview.share}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="empty-preview">
                <div className="empty-preview__visual" aria-hidden="true">
                  <span className="corner corner--tl" />
                  <span className="corner corner--tr" />
                  <span className="corner corner--bl" />
                  <span className="corner corner--br" />
                  <QrCode size={72} strokeWidth={1.25} />
                </div>
                <h3>{t.preview.emptyTitle}</h3>
                <p>{t.preview.emptyDescription}</p>
              </div>
            )}

            {hasResultError && (
              <p className="result-error" role="alert">
                {t.resultError}
              </p>
            )}
          </section>
        </section>

        <section className="feature-strip" aria-label={t.features.aria}>
          <Feature
            icon={<ShieldCheck size={21} />}
            title={t.features.items[0].title}
            detail={t.features.items[0].detail}
          />
          <Feature
            icon={<Smartphone size={21} />}
            title={t.features.items[1].title}
            detail={t.features.items[1].detail}
          />
          <Feature
            icon={<Check size={21} />}
            title={t.features.items[2].title}
            detail={t.features.items[2].detail}
          />
        </section>
      </main>

      <footer>
        <p>EasyQR</p>
        <span aria-hidden="true">·</span>
        <p>{t.footer}</p>
      </footer>

      <div className={`toast${toast ? ' toast--visible' : ''}`} aria-live="polite" aria-atomic="true">
        {toast && (
          <>
            <span className={`toast__icon toast__icon--${toast.tone}`} aria-hidden="true">
              {toast.tone === 'success' ? <Check size={17} /> : <Sparkles size={17} />}
            </span>
            <span>{t.toasts[toast.key]}</span>
          </>
        )}
      </div>
    </div>
  );
}
